import { watch } from "fs";
import { useEffect, useMemo, useRef, useState } from "react";
import useModal from "../hooks/useModal";
import Button from "./form-controls/Button";
import Input from "./form-controls/Input";
import TextArea from "./form-controls/TextArea";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import InvalidFeedback from "./InvalidFeedback";
import { AccountService } from "../services/AccountsService";
import { useCurrentAccount } from "../hooks/useCurrentAccount";
import { APPS } from "../constants";
import Select from 'react-select'
import { MetaService } from "../services/MetaService";
import { ReportService } from "../services/ReportsService";
import { AlertService } from "../services/Alert";

interface ModalNewConversationProps {
    show: boolean;
    ticket_id: string;
    customer_number: string;
    onRequestClose: () => void;
}

function ModalNewConversation({ show, ticket_id, customer_number, onRequestClose }: ModalNewConversationProps) {

    const { account } = useCurrentAccount();

    const modalRef = useRef<HTMLDivElement>(null)

    useModal(modalRef, show)

    const [channels, setChannels] = useState([])
    const [templates, setTemplates] = useState([])
    const [template, setTemplate] = useState([])

    const defaultValues = {
        channel: '',
        template: '',
        message: '',

        variables: [],
    }
    const { control, register, handleSubmit, watch, reset, formState: { errors, isValid, isSubmitting } } = useForm({
        mode: 'onChange',
        defaultValues
    });

    const templateParameters = useFieldArray({
        control,
        name: 'variables',
    });

    const channelSelected: any = watch('channel')
    const templateSelected: any = watch('template')


    const header = useMemo(() => template.find( t => t.type === 'HEADER'), [template])
    const body = useMemo(() => template.find( t => t.type === 'BODY'), [template])
    const footer = useMemo(() => template.find( t => t.type === 'FOOTER'), [template])
    const buttons = useMemo(() => template.find( t => t.type === 'BUTTONS'), [template])

   
    const buildTemplateVariables = (data) => {
        let headerComponent = []
        let bodyComponent = []

        if(header && header.format !== 'TEXT') {
            const format = header.format.toLowerCase();
            headerComponent.push({
                type: 'header',
                parameters: [{
                    type: format,
                    [format]: {
                        link: header[format]
                    }
                }]
                
            })
        }

        for (const row of data) {
            bodyComponent[+row.label - 1] = {
                type: "text",
                text: row.value
            }
        }

        let variables = []

        if(headerComponent.length) {
            variables.push(headerComponent[0])
        }
        if(bodyComponent.length) {
            variables.push({
                type: "body",
                parameters: bodyComponent
            })
        }

        return JSON.stringify(variables);
    }

    const buildMessageFromTemplate = (data) => {       
    
        const message_template = [];
    
        const escapeRegExp = (value: string): string => {
            return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        };
    
        if (header && header.format === 'TEXT') {
            let header_string = header.text;

            message_template.push(`*${header_string}*`);
        }
    
        let body_string = body.text;
    
        for (const row of data) {
            body_string = body_string.replace(new RegExp(escapeRegExp(`{{${row.label }}}`), 'g'), row.value );
        }

        message_template.push(`${body_string}`);
    
        if (footer) {
            const footer_string = footer.text;
            message_template.push(`${footer_string}`);
        }
    
        if (buttons) {
          const btns = buttons.buttons.map( btn => {
            if (btn.type === 'QUICK_REPLY') {
              return `- ${btn.text}`;
            }
            if (btn.type === 'URL') {
              return `- ${btn.text}(${btn.url})`;
            }
            if (btn.type === 'PHONE_NUMBER') {
              return `- ${btn.text}(${btn.phone_number})`;
            }
          });
          message_template.push(btns.join('\n'));
        }
    
        return message_template.join('\n\n');
    }

    const submit = async (data) => {
        try {
            const params = {
                ticket_id: ticket_id,
                app: data.channel.name,
                message: data.channel.value === APPS.WHATSAPP_STANDAR ? data.message: buildMessageFromTemplate(data.variables),
                template_id: data.template?.value,
                variables: buildTemplateVariables(data.variables),
            }

            await ReportService.createNewConversation(account.id, params)
            AlertService.toastSuccess('Conversación abierta con éxito.')
            onRequestClose()
        } catch (error) {
            console.error(error);
            AlertService.error('¡Error!', typeof error === 'string' ? error: 'Ocurrió un error reabrir el ticket, intenta nuevamente')
        }
    }

    const getChannels = async () => {
        const { data } = await AccountService.getChannels(account.id)

        const whatsappStandar = data.find(c => c.app.toUpperCase() === APPS.WHATSAPP_STANDAR)
        const whatsappBusiness = data.find(c => c.app.toUpperCase() === APPS.WHATSAPP_BUSINESS && (c.cm_wab_id || c.meta_waba_id))

        let _channels_ = [];

        if (whatsappStandar) {
            _channels_.push({
                value: APPS.WHATSAPP_STANDAR,
                label: 'WhatsApp Estandar',
                name: whatsappStandar.app
            })
        }
        if (whatsappBusiness) {
            _channels_.push({
                value: APPS.WHATSAPP_BUSINESS,
                label: 'WhatsApp Business',
                name: whatsappBusiness.app
            })
        }

        setChannels(_channels_)
    }

    const getTemplates = async () => {
        const { data } = await MetaService.getTemplates(account.id, 'APPROVED')

        setTemplates(data.map(row => ({ value: row.id, label: `${row.name} (${row.language})`, data: row })))
    }

    useEffect(() => {
        if (show) {
            getChannels()
            getTemplates()
        } else {
            reset(defaultValues)
        }
    }, [show])

    useEffect(() => {

        let templateSelectedCopy: any = templateSelected;
        
        setTemplate(templateSelectedCopy ? JSON.parse(templateSelectedCopy.data.body) as any: [] )
    
    }, [templateSelected])

    useEffect(() => {
        templateParameters.remove()       

        if (!body) {
            return;
        }

        const regex = /{{[0-9]}}/g;

        let tokens: any = body.text.match(regex);
        if (!tokens) {
            return;
        }

        tokens = tokens
            .map(t => t.replace(/{{([0-9])}}/g, '$1'))
            .sort()
            .filter((value, index, array) => array.indexOf(value) === index)

        for (const token of tokens) {
            templateParameters.append({
                label: token,
                value: '',
            }, { shouldFocus: false })
        }
        
    }, [body])


    return (
        <div className="modal fade" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" >
            <div className="modal-dialog " role="document">

                <div className="modal-content">
                    <div className="modal-header ">
                        <h4 className="modal-title" ><i className="bi-chat-left-dots me-2"></i> Nueva conversación</h4>

                        <button type="button" className="btn-close" onClick={onRequestClose} />
                    </div>
                    <div className="modal-body">

                        <p>Inicia una nueva conversación con {customer_number}</p>

                        <div className="mb-3">
                            <label className="form-label" >Canal *</label>
                            <Controller
                                control={control}
                                name='channel'
                                rules={{ required: true }}
                                render={
                                    ({
                                        field: { onChange, value },
                                        fieldState: { error }
                                    }) => {
                                        const currentValue: any = value;

                                        return (
                                            <>
                                                <Select
                                                    options={channels}
                                                    className={error ? 'form-select-custom is-invalid' : ''}
                                                    classNamePrefix='form-select-custom'
                                                    placeholder='Selecciona el canal de salida'
                                                    onChange={(option) => onChange(option)}
                                                    value={channels.find(channel => channel.value === currentValue.value) || ''}
                                                />
                                                <InvalidFeedback error={error} />
                                            </>
                                        )
                                    }
                                }
                            />
                        </div>
                       
                        {
                            channelSelected?.value === APPS.WHATSAPP_STANDAR && (
                                <div className="mb-3">
                                    <TextArea
                                        label='Mensaje *'
                                        errors={errors.message}
                                        value={watch('message').length}
                                        counter={1024}
                                        {...register('message', { required: true, maxLength: { value: 256, message: '256' } })} />
                                </div>
                            )
                        }
                        {
                            channelSelected?.value === APPS.WHATSAPP_BUSINESS && (
                                <div className="mb-3">
                                    <label className="form-label" >Plantilla *</label>
                                    <Controller
                                        control={control}
                                        name='template'
                                        rules={{ required: true }}
                                        render={
                                            ({
                                                field: { onChange, value },
                                                fieldState: { error }
                                            }) => {
                                                return (
                                                    <>
                                                        <Select
                                                            options={templates}
                                                            className={'form-select-custom ' + (error ? ' is-invalid' : '')}
                                                            classNamePrefix='form-select-custom'
                                                            placeholder='Plantilla de mensaje'
                                                            onChange={(data: any) => onChange(data)}
                                                            value={value as any}
                                                        />
                                                        <InvalidFeedback error={error} />
                                                    </>
                                                )
                                            }
                                        }
                                    />
                                </div>
                            )
                        }

                        {
                            templateParameters.fields.length > 0 && (
                                <div className="mb-3">
                                    <div className="alert alert-soft-secondary" role="alert">
                                        <h5>Variables de plantilla</h5>
                                        <p>La plantilla que seleccionaste requiere de variables extra antes de ser enviado.</p>

                                        <h6>Variable</h6>

                                        {
                                            templateParameters.fields.map((field, index) => (
                                                <div className="d-flex align-items-center mb-2" key={field.id}>
                                                    <label className="form-label mx-3" >{`{{${field.label}}}`}</label>
                                                    <div className="flex-grow-1">
                                                        <Input  errors={errors.variables?.[index] } {...register(`variables.${index}.value`, { required: true })}   />
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            )
                        }


                        {
                            channelSelected?.value === APPS.WHATSAPP_BUSINESS && templateSelected && (
                                <div className="bg-whatsapp rounded-1">
                                    <div className="p-4">
                                        <h4 className="mb-3">Vista previa</h4>
                                        <div className="preview-template ">
                                            {header && header.format === 'TEXT' && <div className="preview-template-header">{header.text}</div>}
                                            {header && header.format === 'IMAGE' && <div className="preview-template-header"><div className="preview-template-image" style={{ backgroundImage: `url(${header.image})` }}>{!header.image && <i className="bi-card-image"></i>}</div></div>}
                                            {header && header.format === 'VIDEO' && <div className="preview-template-header"> <div className="preview-template-video" > <i className="bi-play-circle-fill"></i></div> </div>}
                                            {header && header.format === 'DOCUMENT' && <div className="preview-template-header"> <div className="preview-template-video" > <i className="bi-filetype-pdf"></i></div> </div>}
                                            {body && <div dangerouslySetInnerHTML={{ __html: body.text.replace(/\n/g, '<br/>') }}></div>}
                                            {footer && <div className="preview-template-footer">{footer.text}</div>}
                                        </div>
                                        {
                                            buttons && (
                                                <div className="">
                                                    {buttons.buttons.map((btn, index) => (<div key={index} className="preview-template preview-template-btn">{btn.text}</div>))}
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            )
                        }

                    </div>
                    <div className="modal-footer">
                        <button type="button" className="btn btn-white btn-sm" onClick={onRequestClose} >Cerrar</button>
                        <Button type="submit" className="btn btn-primary btn-sm" disabled={!isValid} isLoading={isSubmitting} onClick={handleSubmit(submit)}>Enviar mensaje</Button>
                    </div>
                </div>

            </div>
        </div>
    )
}
export default ModalNewConversation